import PageTitle from "../../../components/PageTitle";
import Button from "../../../components/Button";
import { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Table from "../../../components/Table/Table";
import { TableSearch } from "../../../components/Table/TableActions";
import Modal from "../../../components/Modal";
import useStyles from "../styles";
import { Grid } from "@mui/material";
import SelectGroup from "../../../components/FormComponent/SelectGroup";
import { httprequest } from "../../../data/api";
import useValidator from "../../../hooks/useValidator";
import Badge from "../../../components/Badge";
import { useUserState } from "../../../context/UserContext";
import useWindowSize from "../../../hooks/useWindow";

const GET_ALL_SUBJECTS = "/api/v1/subjects";
const LESSON_NOTE = "/api/v1/lesson-outlines/reviewer";
const TEACHER = "/api/v1/employees";

const AdminLessonNote = () => {
  const navigate = useNavigate();
  const { userRole } = useUserState();
  const isAdmin = userRole?.role_type === "school_admin";
  const [searchResult, setSearchResult] = useState([]);
  const [createNoteModal, setCreateNoteModal] = useState(false);
  const [allSubjects, setAllSubjects] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [classValidator, showClassValidator] = useValidator();
  const [allLessonNotes, setAllLessonNotes] = useState([]);
  const [lessonNoteId, setLessonNoteId] = useState("");
  const [lessonNoteForm, setLessonNoteForm] = useState({
    subject_id: "",
    class_id: ""
  });
  const [editLessonNoteForm, setEditLessonNoteForm] = useState({
    subject_id: "",
    class_id: ""
  });
  const classes = useStyles();
  const [editModal, setEditModal] = useState(false);
  const [isAssignReviewModalOpen, setIsAssignReviewModalOpen] = useState(false);
  const [teachers, setTeachers] = useState([]); // State for teachers
  const [selectedTeacher, setSelectedTeacher] = useState("");
  const [currentRow, setCurrentRow] = useState({});

  const width = useWindowSize();
  const mobile = width < 600;

  /**
 * Handle change event for select inputs in the lesson note form.
 * Updates the lessonNoteForm state with the selected value.
 * @param {event parameter passed on handleChange} e - The select change event.
 */
  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setLessonNoteForm((prev) => ({ ...prev, [name]: value }));
  };

  /**
 * Handle change event for the teacher select input in the edit modal.
 * Updates the selectedTeacher state with the chosen teacher's value.
 * @param {event parameter passed on handleChange} e - The select change event.
 */
  const handleTeacherSelectChange = (e) => {
    setSelectedTeacher(e.target.value);
  };

  /**
 * Toggle the create note modal visibility state.
 */
  const handleCreateNoteModal = () => {
    setCreateNoteModal(!createNoteModal);
  };
  /**
 * Close the assign review modal and reset the selected teacher.
 */
  const closeAssignReviewModal = () => {
    setSelectedTeacher(""); // Reset selected teacher
    setIsAssignReviewModalOpen(false);
  };

  /**
 * Toggle the assign review modal visibility and set the current row for review.
 * @param {Object} row - The lesson note data row to assign a review.
 */
  const handleAssignReviewModal = (row) => {
    setCurrentRow(row);
    setIsAssignReviewModalOpen(!isAssignReviewModalOpen);
  };

  /**
 * Fetch all subjects from the API and update the allSubjects state.
 * Displays an error toast on failure.
 */
  const getAllSubjects = () => {
    httprequest
      .get(GET_ALL_SUBJECTS)
      .then((res) => {
        setAllSubjects(Array.isArray(res?.data?.data) ? res?.data?.data : []);
      })
      .catch((err) => {
        const errorMsg = err?.response?.data?.message || (typeof err?.response?.data === "string" ? err?.response?.data : "An error occurred");
        toast.error(errorMsg);
      });
  };

  /**
 * Fetch all lesson notes from the API and update the allLessonNotes and searchResult states.
 * Displays an error toast on failure.
 */
  const getAllLessonNotes = () => {
    httprequest
      .get(`${LESSON_NOTE}`
      )
      .then((res) => {
        const data = Array.isArray(res?.data?.data) ? res?.data?.data : [];
        setAllLessonNotes(data);
        setSearchResult(data);
      })
      .catch((err) => {
        const errorMsg = err?.response?.data?.message || (typeof err?.response?.data === "string" ? err?.response?.data : "An error occurred");
        toast.error(errorMsg);
      });
  };

  /**
 * Fetch all teachers from the API and update the teachers state.
 * Displays an error toast on failure.
 */
  const getAllTeachers = () => {
    httprequest
      .get(TEACHER)
      .then((res) => {
        setTeachers(Array.isArray(res?.data?.data?.employees) ? res?.data?.data?.employees : []);
      })
      .catch((err) => {
        const errorMsg = err?.response?.data?.message || (typeof err?.response?.data === "string" ? err?.response?.data : "An error occurred");
        toast.error(errorMsg);
      });
  };

  /**
 * Assign the selected teacher as a reviewer for the current lesson note.
 * Shows success or error toasts based on API response.
 * Validates that a teacher is selected before sending the request.
 */
  const handleAssignTeacher = () => {
    if (selectedTeacher) {
      setIsLoading(true);
      httprequest
        .post(`/api/v1/lesson-outlines/${currentRow?.version_id}/review`, { reviewer: selectedTeacher })
        .then((res) => {
          toast.success(res?.data?.message);
          closeAssignReviewModal();
          setIsLoading(false);
        })
        .catch((err) => {
          toast.error(err?.response?.data?.message);
          setIsLoading(false);
        });
    } else {
      toast.error("Please select a teacher.");
    }
  };

  /**
 * Create a new lesson note by posting the form data to the API.
 * Validates form input before submitting.
 * On success, reloads lesson notes, toggles modal, and shows a success toast.
 * Shows validation errors or API error messages as toast notifications.
 */
  const handleCreateLessonNote = () => {
    if (classValidator.allValid()) {
      setIsLoading(true);
      httprequest
        .post(LESSON_NOTE, lessonNoteForm)
        .then((res) => {
          toast.success(res?.data?.message);
          getAllLessonNotes();
          handleCreateNoteModal();
          setIsLoading(false);
        })
        .catch((err) => {
          toast.error(err?.response?.data?.message);
        });
    } else {
      showClassValidator(true);
    }
  };

  /**
 * Close the edit lesson note modal and reset the edit form and selected lesson note ID.
 */
  const closeEditModal = () => {
    setEditLessonNoteForm({ ...lessonNoteForm, subject_id: "", class_id: "" });
    setLessonNoteId("");
    setEditModal(false);
  };

  /**
 * Send a PATCH request to update a lesson note with the edited form data.
 * On success, reloads lesson notes and closes the edit modal.
 * Displays error toast on failure.
 * @param {string} lessonNoteId - The ID of the lesson note to patch.
 */
  const handlePatchLessonNote = (lessonNoteId) => {
    httprequest
      .patch(`${LESSON_NOTE}/${lessonNoteId}`, editLessonNoteForm)
      .then((res) => {
        toast.success(res?.data?.message);
        getAllLessonNotes();
        closeEditModal();
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message);
      });
  };

  /**
 * Navigate to the lesson notes review page for a specific lesson outline.
 * @param {Object} row - The lesson note row containing lesson_outline_id.
 */
  const navigateViewLesson = (row) => {
    navigate(`/lesson-notes-review/${row?.lesson_outline_id}`);
  };

  const columns = [
    {
      name: "Class",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Class</p>}
          {row?.class?.name || "--/--"}
        </>
      )
    },
    {
      name: "Subject",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Subject</p>}
          {row?.subject?.name || "--/--"}
        </>
      )
    },
    {
      name: "Status",
      center: "true",
      cell: (row) => (<Badge status={row?.status} />)
    },
    {
      name: "Date Submitted",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Date Submitted</p>}
          {row?.created_at?.substring(0, 10) || "--/--"}
        </>
      )
    },
    {
      name: "Date Updated",
      selector: (row) => (
        <>
          {mobile && <p className="mobile-styling">Date Updated</p>}
          {row?.updated_at?.substring(0, 10) || "--/--"}
        </>
      )
    },
    {
      name: "",
      center: "true",
      sortable: false,
      cell: (row) => (
        <div key={row?.id} className="table-action-container">
          <button
            className="table-action-icon"
            onClick={() => navigateViewLesson(row)}
          >
            <Icon icon="fluent:eye-16-regular" />
          </button>
          {isAdmin && (row?.status !== "approved" && row?.status !== "rejected") && (
            <button className="table-action-icon" onClick={() => handleAssignReviewModal(row)}>
              <Icon icon="hugeicons:user-add-02" />
            </button>
          )}
        </div>
      )
    }
  ];

  useEffect(() => {
    if (isAdmin) {
      getAllTeachers();
      getAllSubjects();
    }
  }, [isAdmin]);

  useEffect(() => {
    getAllLessonNotes();
  }, []);

  /**
 * Filter lesson notes based on the search input.
 * Filters by matching every word in the input against class or subject name.
 * @param {event parameter passed on handleChange} e - The search input change event.
 */
  const handleLessonNoteSearch = (e) => {
    const { value } = e.target;
    const valArray = value.split(" ");
    // --| Filter data by partial match onchange in the search input box
    const result = allLessonNotes?.filter((data) => valArray?.every(
      (word) => data?.class?.name?.toLowerCase().includes(word.toLowerCase()) ||
      data?.subject?.name?.toLowerCase().includes(word.toLowerCase())
    )
    );
    setSearchResult(result);
  };

  const { subject_id } = lessonNoteForm;

  return (
    <div>
      <PageTitle
        title="Lesson Notes Reviews"
        subtitle="See all lesson notes sent to you for review."
        button={
          <div className="flex gap-10">
            {/* <PopModal title="Lesson Note" videoUrl="https://youtu.be/a1BUt3ZlZBg" /> */}
          </div>
        }
      />
      <div className="page-content">
        <Table
          data={searchResult}
          columns={columns}
          subHeader={true}
          pagination
          subHeaderComponent={
            allLessonNotes?.length > 0 && (
              <TableSearch
                placeholder="Search here..."
                searchTableFunc={handleLessonNoteSearch}
              />
            )}
        />
        {/* <ToastContainer /> */}
      </div>
      <Modal
        title="Create Lesson Note"
        modalContent={
          <>
            <Grid container spacing={2}>
              <Grid item lg={6} md={6} sm={12} xs={12}>
                <SelectGroup
                  label="Subject"
                  required
                  children={
                    <select onChange={handleSelectChange} name="subject_id">
                      <option value="">--- Select ---</option>
                      {allSubjects?.map((subject) => {
                        return (
                          <option key={subject?.id} value={subject?.id}>
                            {subject?.name}
                          </option>
                        );
                      })}
                    </select>
                  }
                />
                {classValidator.message("subject_id", subject_id, "required")}
              </Grid>
            </Grid>
          </>
        }
        modalFooter={
          <>
            <div className={classes.modalFooterBtn}>
              <Button
                variant="primary"
                buttonSize="full"
                color="btndefault"
                buttonName="Create Lesson Note"
                onClick={handleCreateLessonNote}
                isLoading={isLoading}
              />
            </div>
          </>
        }
        isopen={createNoteModal}
        closeModal={() => {
          setCreateNoteModal(false);
        }}
      />
      {/* Edit Modal */}
      <Modal
        title="Assign Lesson Note"
        modalContent={
          <>
            <Grid container spacing={2}>
              <Grid>
                <SelectGroup
                  label="Teachers Name"
                  required
                  children={
                    <select onChange={handleTeacherSelectChange} value={selectedTeacher}>
                      <option value="">--- Select Teacher ---</option>
                      {teachers?.map((teacher) => (
                        <option key={teacher?.id} value={teacher?.id}>
                          {teacher?.name}
                        </option>
                      ))}
                    </select>
                  }
                />
              </Grid>
            </Grid>
          </>
        }
        modalFooter={
          <>
            <div className={classes.modalFooterBtn}>
              <Button
                variant="primary"
                buttonSize="full"
                color="btndefault"
                buttonName="Edit Lesson Note"
                onClick={() => handlePatchLessonNote(lessonNoteId)}
                isLoading={isLoading}
              />
            </div>
          </>
        }
        isopen={editModal}
        closeModal={closeEditModal}
      />
      <Modal
        title="Assign Lesson Note Review"
        subtitle="Assign lesson note review to a teacher."
        modalContent={
          <>
            <Grid>
              <SelectGroup
                label="Teachers Name"
                required
                children={
                  <select onChange={handleTeacherSelectChange} value={selectedTeacher}>
                    <option value="">--- Select Teacher ---</option>
                    {teachers?.map((teacher) => (
                      <option key={teacher?.user_id} value={teacher?.user_id}>
                        {teacher?.first_name} {teacher?.last_name}
                      </option>
                    ))}
                  </select>
                }
              />
            </Grid>
          </>
        }
        modalFooter={
          <>
            <div className="flex gap-10">
              <Button
                variant="primaryoutline"
                buttonName="Cancel"
                buttonSize="full"
                color="btnfontprimary"
                onClick={closeAssignReviewModal}
              />
              <Button
                variant="primary"
                buttonName="Confirm"
                buttonSize="full"
                color="btndefault"
                onClick={handleAssignTeacher}
                isLoading={isLoading}
              />
            </div>
          </>
        }
        isopen={isAssignReviewModalOpen}
        closeModal={closeAssignReviewModal}
      />
    </div>
  );
};

export default AdminLessonNote;
